import {
    Image,
    ImageWithTranslatedAlt,
    PrepCheckComponentRating,
    PrepCheckScope,
    PrepCheckStatus,
    TranslatedString,
} from './Constants'
import { SupportedHazardFilter, SupportedHazardStage } from './Hazards'

export interface PrepCheckBase {
    id: number
    hazard_id: number
    title: TranslatedString
    scope: PrepCheckScope
    date_updated: Date
}

export interface PrepCheckOverview extends PrepCheckBase {
    dark_icon?: ImageWithTranslatedAlt
    light_icon?: ImageWithTranslatedAlt
    filters: SupportedHazardFilter[]
}

export interface PrepCheckOverviewDTO
    extends Omit<PrepCheckOverview, 'title' | 'dark_icon' | 'light_icon'> {
    title: string
    dark_icon?: Image
    light_icon?: Image
}

export interface PrepCheck extends Omit<PrepCheckOverview, 'filters'> {
    questions: PrepCheckQuestion[]
    novice_badge: ImageWithTranslatedAlt | null
    intermediate_badge: ImageWithTranslatedAlt | null
    advanced_badge: ImageWithTranslatedAlt | null
}

export interface PrepCheckDTO extends Omit<PrepCheckOverviewDTO, 'filters'> {
    questions: PrepCheckQuestionDTO[]
}

export interface PrepCheckWithAnswers extends PrepCheck {
    answer_key: PrepCheckAnswerKey[]
}

export interface PrepCheckAnswerKey {
    question_id: number
    answers: PrepCheckAnswer[]
}

export type PrepCheckQuestionType =
    | 'OneD_Ordered_List' // 1-dimensional ordering (i.e. just an ordered list)
    | 'TwoD_Grid_Placement' // 2-dimensional ordering (e.g. sort by hazard stage AND order of action)
    | 'Checkboxes' // question with checkbox options

export interface PrepCheckQuestion {
    id: number
    name: TranslatedString
    question: TranslatedString
    subheading?: TranslatedString
    explainer_text?: TranslatedString
    options: PrepCheckAnswerOption[]
    image?: ImageWithTranslatedAlt
    question_type: PrepCheckQuestionType
    link_to_emergency_items: boolean
}

export interface PrepCheckQuestionDTO {
    id: number
    name: string
    question: string
    subheading?: string
    explainer_text?: string
    options: PrepCheckAnswerOptionDTO[]
    image?: Image
    question_type: PrepCheckQuestionType
    link_to_emergency_items: boolean
}

export interface PrepCheckAnswerOption {
    id: number // Answer option ID
    text: TranslatedString
    explainer_text?: TranslatedString
    image?: ImageWithTranslatedAlt
    weight: number
    best_order?: number
    best_stage?: SupportedHazardStage
}

export interface PrepCheckAnswerOptionDTO
    extends Omit<PrepCheckAnswerOption, 'text' | 'explainer_text' | 'image'> {
    text: string
    explainer_text?: string
    image?: Image
}

interface PrepCheckAnswerBase {
    id: number // Matches id of PrepCheckAnswerOption
}

export interface PrepCheckDND1DAnswerItem extends PrepCheckAnswerBase {
    order: number // Identifies a 1D DND answer
}

export enum PrepCheckDND2DAnswerResult {
    CORRECT = 'correct',
    RIGHT_PHASE = 'right-phase',
    WRONG_PHASE = 'wrong-phase',
    NOT_PLACED = 'not-placed',
}

export interface PrepCheckDND2DAnswerItem extends PrepCheckAnswerBase {
    placement: [string, number] // Identifies a 2D DND answer
    result?: PrepCheckDND2DAnswerResult
}

export interface PrepCheckCheckboxAnswerItem extends PrepCheckAnswerBase {
    checked: boolean // Identifies a checkbox-style answer
}

export interface PrepCheckAnswer<
    T =
        | PrepCheckDND1DAnswerItem
        | PrepCheckDND2DAnswerItem
        | PrepCheckCheckboxAnswerItem,
> {
    answer_id: number
    answer: T
}

export interface JourneyPhaseDTO {
    name: string
    subcopy: string
    icon: Image
}

export interface PrepCheckResultOverview {
    id: number
    prep_check_id: number
    hazard_id: number
    score: number
    badge?: Image
    badgeName: string
    newBadge: boolean
    badgeAwardText: string
    newJourneyPhaseAwardText?: string
    // preparednessLevel: string
    darkPrepCheckIcon?: Image
    lightPrepCheckIcon?: Image
    prepCheckTitle: string
    dateLastUpdated: string
    newJourneyPhase?: JourneyPhaseDTO
}

export interface PrepCheckResults extends PrepCheckResultOverview {
    questions: PrepCheckResultItem[]
}

export interface PrepCheckResultsDTO
    extends Omit<PrepCheckResults, 'questions'> {
    questions: PrepCheckResultItemDTO[]
}

export interface PrepCheckUpNextDTO {
    id: number
    name: string
    icon: Image
}

export interface JourneyPhase {
    id: number
    name: string // non-translated name from Strapi maps to this; not used in UI, but used behind-the-scenes to connect to DB entities
    display_name: TranslatedString // "name" and "translated_name" from Strapi map to this
    icon: ImageWithTranslatedAlt
    point_threshold: number
    subcopy: TranslatedString
}

export interface PrepCheckUserStats {
    journeyPhase: string
    journeyPhaseIcon: Image
    journeyPhaseSubcopy: string
    numCompleted: number
    numTotal: number
}

export interface PrepCheckUserSubmission {
    id: number
    prep_check_id: number
    submitted_responses: PrepCheckQuestionSubmission[]
    status: PrepCheckStatus
    current_zip?: string
}

export interface ScoredPrepCheckUserSubmission extends PrepCheckUserSubmission {
    score: number
}

export interface PrepCheckUserSubmissionDTO
    extends Omit<PrepCheckUserSubmission, 'id'> {
    id?: number
}

export interface PrepCheckQuestionSubmission<
    T =
        | PrepCheckCheckboxAnswerItem
        | PrepCheckDND1DAnswerItem
        | PrepCheckDND2DAnswerItem,
> {
    question_id: number
    user_answer: PrepCheckAnswer<T>[]
}

export interface ScoredPrepCheckQuestionSubmission
    extends PrepCheckQuestionSubmission {
    score: number
}

export interface PrepCheckScoreDTO {
    overall: number
    questions: PrepCheckQuestionScoreDTO[]
}

export interface PrepCheckQuestionScoreDTO {
    question_id: number
    score: number
}

export interface PrepCheckResultItem extends PrepCheckQuestionSubmission {
    question_name: TranslatedString
    question_text: TranslatedString
    question_image?: ImageWithTranslatedAlt
    best_answer: PrepCheckAnswer[]
    breakdown_text: TranslatedString
    result_preview_text?: TranslatedString
    component_rating: PrepCheckComponentRating
}

export interface PrepCheckResultItemDTO extends PrepCheckQuestionSubmission {
    question_name: string
    question_text: string
    question_image?: Image
    best_answer: PrepCheckAnswer[]
    breakdown_text: string
    result_preview_text?: string
    component_rating: PrepCheckComponentRating
}

export interface PrepCheckDraftDetails {
    id: number
    amountDone: number
}
